import { BrowserModule } from '@angular/platform-browser';
import { isDevMode, NgModule, provideBrowserGlobalErrorListeners, provideZoneChangeDetection } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HomeComponent } from './home/home.component';
import { LookupsComponent } from './lookups/lookups.component';
import { LogsComponent } from './logs/logs.component';
import { LoginWrapperComponent } from './login-wrapper/login-wrapper.component';
import { StarbaseLauncherModule } from 'starbase-launcher';
import { StarbaseNotificationModule, StarbaseNotificationService } from 'starbase-notification';
import { StarbaseModalOverlayModule } from 'modal-overlay';
import { StarbaseModalDialogModule } from 'starbase-modal-dialog';
import { AccountManagementModule } from 'account-management';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { ColorCircleModule } from 'ngx-color/circle';
import { MatSliderModule } from '@angular/material/slider';
import { NewTableEditorModule } from 'new-table-editor';
import { StarbaseMenuBarModule } from 'starbase-menu-bar';
import { AppGlobalsServiceBase, ScaleTechCoreModule } from 'scaletech-core';
import { StarbaseLoginModule, StarbaseLoginService, WebApiService } from 'starbase-login';
import { StarbaseWizardModule } from 'starbase-wizard';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { DashboardModule } from 'starbase-dashboard';
import { ContactDashModule } from 'contact-dash';
import { SortablejsModule } from 'ngx-sortablejs';
import { ScaletechHomeModule } from 'scaletech-home';
import { ScaletechVehiclesModule } from 'scaletech-vehicles';
import { ScaletechTicketsModule} from 'scaletech-tickets';
import { ScaletechJobsModule } from 'scaletech-jobs';
import { ScaletechWizardsModule } from 'scaletech-wizards';
import { ScaletechPaymentModule } from 'scaletech-payment'
import { environment } from '../environments/environment';
import { AppInsightsService } from './services/app-insights.service';
import { HttpClientModule } from '@angular/common/http';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { ScaletechInvoicesModule } from 'scaletech-invoices';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { ReportsInvoiceWizardComponent } from './reports-invoice-wizard/reports-invoice-wizard.component';
import { AttributionComponent } from './attribution/attribution.component';
import { ReportsComponent } from './reports/reports.component';
import { ReportsWizardComponent } from './reports-wizard/reports-wizard.component';
import { CommonModule, DecimalPipe } from '@angular/common';
import { BatchSelectComponent } from './batch-select/batch-select.component';
import { NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import { ScaletechKioskModule } from 'scaletech-kiosk';
import { CloseComponent } from './close/close.component';
import { EulaComponent } from './eula/eula.component';
import { PrivacyComponent } from './privacy/privacy.component';
import { TransactionReportWizardComponent } from './transaction-report-wizard/transaction-report-wizard.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { AppGlobalsService } from './services/app-globals.service';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { MakePaymentComponent } from './make-payment/make-payment';
import { VoucherHomeComponent } from './voucher-home/voucher-home';

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    LookupsComponent,
    LogsComponent,
    LoginWrapperComponent,
    AttributionComponent,
    ReportsComponent,
    ReportsWizardComponent,
    TransactionReportWizardComponent,
    ReportsInvoiceWizardComponent,
    BatchSelectComponent,
    CloseComponent,
    EulaComponent,
    PrivacyComponent,
    MakePaymentComponent,
    VoucherHomeComponent
  ],
  imports: [
    CommonModule,
    BrowserModule,
    AppRoutingModule,
    StarbaseLauncherModule,
    StarbaseNotificationModule,
    StarbaseModalOverlayModule,
    StarbaseModalDialogModule,
    AccountManagementModule,
    MatTooltipModule,
    TabsModule,
    ColorCircleModule,
    MatSliderModule,
    NewTableEditorModule,
    StarbaseMenuBarModule,
    ScaleTechCoreModule,
    ScaletechHomeModule,
    StarbaseLoginModule,
    StarbaseWizardModule,
    FormsModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatSelectModule,
    DashboardModule,
    ContactDashModule,
    SortablejsModule,
    ReactiveFormsModule,
    ScaletechInvoicesModule,
    ScaletechJobsModule,
    ScaletechVehiclesModule,
    ScaletechTicketsModule,
    ScaletechWizardsModule,
    ScaletechPaymentModule,
    ScaletechKioskModule,
    HttpClientModule,
    MatNativeDateModule,
    MatDatepickerModule,
    MatInputModule,
    BsDatepickerModule,
    BsDropdownModule,
    PopoverModule,
    NgxMaskPipe,
    NgxMaskDirective,
    MatExpansionModule
  ],
  providers: [
    // provideZoneChangeDetection: Required since Angular 21 to disable zoneless behavior for change-detection
    // https://github.com/angular/angular/issues/67498
    provideZoneChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    StarbaseNotificationService,
    { provide: AppGlobalsServiceBase, useExisting: AppGlobalsService },
    {
      provide: StarbaseLoginService,
      useFactory: LoginServiceFactory,
    },
    {
      provide: WebApiService,
      useFactory: WebApiServiceFactory,
      deps: [StarbaseLoginService]
    },
    AppInsightsService,
    DecimalPipe
  ],
  bootstrap: [AppComponent],
})

export class AppModule {
  public static LOGIN_SERVICE_TIMEOUT = 7500; // ms
  public static DEV_API_SERVER_ADDRESS =
    (environment.testMode) ? environment.TESTING_API_SERVER_ADDRESS : environment.PROD_API_SERVER_ADDRESS;
  public static PROD_API_SERVER_ADDRESS =
    (environment.testMode) ? environment.TESTING_API_SERVER_ADDRESS : environment.PROD_API_SERVER_ADDRESS;
}

export function LoginServiceFactory() {
  return new StarbaseLoginService(AppModule.LOGIN_SERVICE_TIMEOUT);
}

export function WebApiServiceFactory(loginSvc: StarbaseLoginService) {
  if (isDevMode()) {
    document.title = `Test edition ${document.title}`;
    return new WebApiService(loginSvc, AppModule.DEV_API_SERVER_ADDRESS);
  } else {
    return new WebApiService(loginSvc, AppModule.PROD_API_SERVER_ADDRESS);
  }
}
